import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { LineChart } from '@mui/x-charts';
import { useNavigate } from 'react-router-dom';
import { getBattleAgentsStatsOverview } from '../Api';
import type {
    BattleAgentsStatsOverview,
    BattleAgentsStatsSlice,
    BattleAgentsStatsWindow,
} from '../models/dtos/BattleAgentDto';

function pct(rate: number): string {
    return `${(rate * 100).toFixed(1)}%`;
}

function SliceBlock({ title, slice }: { title: string; slice: BattleAgentsStatsSlice }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{title}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip size="small" label={`Created ${slice.created}`} />
                <Chip size="small" color="success" label={`Played ${slice.played}`} />
                <Chip size="small" color="warning" label={`Pending ${slice.pending}`} />
                <Chip size="small" label={`Dead ${slice.dead}`} />
                <Chip size="small" color="info" label={`Play rate ${pct(slice.playRate)}`} />
            </Stack>
        </Box>
    );
}

function WindowCard({ window }: { window: BattleAgentsStatsWindow }) {
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Last {window.days} day{window.days === 1 ? '' : 's'}</Typography>
                <Stack spacing={2}>
                    <SliceBlock title="All battles" slice={window.total} />
                    <SliceBlock title="Agent-created (SUCCESS run)" slice={window.agent} />
                    <SliceBlock title="Organic (not agent run)" slice={window.organic} />
                    <Typography variant="caption" color="text.secondary">Secondary (bot-account creators)</Typography>
                    <SliceBlock title="Bot-account creator" slice={window.botAccount} />
                    <SliceBlock title="Non-bot creator" slice={window.nonBotAccount} />
                </Stack>
            </CardContent>
        </Card>
    );
}

export default function BattleAgentsStatsPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [data, setData] = React.useState<BattleAgentsStatsOverview | null>(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        const result = await getBattleAgentsStatsOverview();
        if (!result) {
            setError('Failed to load battle agent stats.');
            setData(null);
        } else {
            setData(result);
        }
        setLoading(false);
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    const chartDates = data?.dailyLast30Days.map(d => new Date(d.date + 'T00:00:00Z')) ?? [];

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
                    <Typography variant="h4">Battle agents — stats</Typography>
                    <Stack direction="row" spacing={1}>
                        <Button onClick={() => navigate('/battle-agents')}>Agents</Button>
                        <Button onClick={() => navigate('/battle-agents/history')}>History</Button>
                        <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
                    </Stack>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                    Rolling windows (1 / 7 / 30 days). <strong>Agent-created</strong> = challenge linked to a SUCCESS
                    create run (mode PUBLIC/PRIVATE). <strong>Organic</strong> = everything else (including AUTO_ACCEPT
                    of human battles). Secondary split uses configured bot account IDs as creators
                    ({data?.botUserIdsCount ?? '…'} bots). Played = ACCEPTED / STARTED / FINISHED.
                </Typography>

                {error && <Alert severity="error">{error}</Alert>}
                {loading && <CircularProgress />}

                {!loading && data && (
                    <>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                                gap: 2,
                            }}
                        >
                            {data.windows.map(w => (
                                <WindowCard key={w.days} window={w} />
                            ))}
                        </Box>

                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>Last 30 days — created vs played</Typography>
                                {chartDates.length > 0 ? (
                                    <LineChart
                                        height={360}
                                        xAxis={[{ scaleType: 'time', data: chartDates }]}
                                        series={[
                                            { data: data.dailyLast30Days.map(d => d.createdAgent), label: 'Created agent' },
                                            { data: data.dailyLast30Days.map(d => d.createdOrganic), label: 'Created organic' },
                                            { data: data.dailyLast30Days.map(d => d.playedAgent), label: 'Played agent' },
                                            { data: data.dailyLast30Days.map(d => d.playedOrganic), label: 'Played organic' },
                                        ]}
                                    />
                                ) : (
                                    <Typography color="text.secondary">No daily data.</Typography>
                                )}
                            </CardContent>
                        </Card>

                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>Daily breakdown</Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell align="right">Created total</TableCell>
                                            <TableCell align="right">Created agent</TableCell>
                                            <TableCell align="right">Created organic</TableCell>
                                            <TableCell align="right">Played total</TableCell>
                                            <TableCell align="right">Played agent</TableCell>
                                            <TableCell align="right">Played organic</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {[...data.dailyLast30Days].reverse().map(day => (
                                            <TableRow key={day.date}>
                                                <TableCell>{day.date}</TableCell>
                                                <TableCell align="right">{day.createdTotal}</TableCell>
                                                <TableCell align="right">{day.createdAgent}</TableCell>
                                                <TableCell align="right">{day.createdOrganic}</TableCell>
                                                <TableCell align="right">{day.playedTotal}</TableCell>
                                                <TableCell align="right">{day.playedAgent}</TableCell>
                                                <TableCell align="right">{day.playedOrganic}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </>
                )}
            </Stack>
        </Box>
    );
}
